import { SiteClient } from "./site.js";
export class BusinessClient extends SiteClient {
    /** Issues a one-off invoice (defaults to the build fee when no price/amount). */
    async createInvoice(input) {
        return this.request("POST", "/v1/billing/invoice", input, true);
    }
    /** Public dealership inventory for a site slug. */
    async listListings(slug) {
        const res = await this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/listings`);
        return res.listings;
    }
    async getListing(slug, id) {
        return this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/listings/${encodeURIComponent(id)}`);
    }
    /** Public sector-agnostic booking. dropOffAt and pickUpAt are the customer window. */
    async createBooking(slug, input) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/bookings`, input);
    }
    /** Open booking start times (RFC3339) for a site; public. `type` is the bookable type slug. */
    async bookingAvailability(slug, type) {
        const q = type ? `?type=${encodeURIComponent(type)}` : "";
        const res = await this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/bookings/availability${q}`);
        return res.slots ?? [];
    }
    async listBookings(slug) {
        const res = await this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/bookings`, undefined, true);
        return res.bookings;
    }
    /** Assign or reassign the associate. Requires content:write on the site. */
    async assignBooking(slug, id, assigneeUserId) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/bookings/${encodeURIComponent(id)}/assign`, { assigneeUserId }, true);
    }
    /** Confirm a requested booking. Requires content:write on the site. */
    async confirmBooking(slug, id) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/bookings/${encodeURIComponent(id)}/confirm`, undefined, true);
    }
    /** Mark a confirmed booking completed. Requires content:write on the site. */
    async completeBooking(slug, id) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/bookings/${encodeURIComponent(id)}/complete`, undefined, true);
    }
    /** Cancel a requested or confirmed booking. Requires content:write on the site. */
    async cancelBooking(slug, id) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/bookings/${encodeURIComponent(id)}/cancel`, undefined, true);
    }
    async createAppointment(slug, input) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/appointments`, input);
    }
    async appointmentAvailability(slug) {
        const res = await this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/appointments/availability`);
        return res.slots;
    }
    async createListing(slug, input) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/listings`, input, true);
    }
    async updateListing(slug, id, input) {
        return this.request("PUT", `/v1/sites/${encodeURIComponent(slug)}/listings/${encodeURIComponent(id)}`, input, true);
    }
    async archiveListing(slug, id) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/listings/${encodeURIComponent(id)}/archive`, undefined, true);
    }
    /**
     * Shop service menu. Public and active-only by default; `includeArchived` is the staff
     * list (content:write) and sends the bearer token.
     * @deprecated Use `client.catalog.services` (#1044); kept as an alias on /v1.
     */
    async listServices(slug, options = {}) {
        const staff = options.includeArchived === true;
        const base = `/v1/sites/${encodeURIComponent(slug)}/services`;
        const res = await this.request("GET", staff ? `${base}?includeArchived=true` : base, undefined, staff);
        return res.services;
    }
    /** @deprecated Use `client.catalog.createService` (#1044); kept as an alias on /v1. */
    async createService(slug, input) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/services`, input, true);
    }
    /** @deprecated Use `client.catalog.updateService` (#1044); kept as an alias on /v1. */
    async updateService(slug, id, input) {
        return this.request("PATCH", `/v1/sites/${encodeURIComponent(slug)}/services/${encodeURIComponent(id)}`, input, true);
    }
    /** @deprecated Use `client.catalog.archiveService` (#1044); kept as an alias on /v1. */
    async archiveService(slug, id) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/services/${encodeURIComponent(id)}/archive`, undefined, true);
    }
    /**
     * Un-archive a service (content:write). 409 when an active service already has the name.
     * @deprecated Use `client.catalog.restoreService` (#1044); kept as an alias on /v1.
     */
    async restoreService(slug, id) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/services/${encodeURIComponent(id)}/restore`, undefined, true);
    }
    async businessSummary() {
        return this.request("GET", "/v1/business/summary", undefined, true);
    }
    async businessLedger() {
        return this.request("GET", "/v1/business/ledger", undefined, true);
    }
    async recordSale(input) {
        return this.request("POST", "/v1/business/sale", input, true);
    }
    async recordExpense(input) {
        return this.request("POST", "/v1/business/expense", input, true);
    }
    async listInvestors() {
        const res = await this.request("GET", "/v1/investors", undefined, true);
        return res.investors;
    }
    async createInvestor(name, email = "") {
        return this.request("POST", "/v1/investors", { name, email }, true);
    }
    async addInvestorTransaction(investorId, input) {
        return this.request("POST", `/v1/investors/${encodeURIComponent(investorId)}/transactions`, input, true);
    }
    async listStaff() {
        const res = await this.request("GET", "/v1/staff", undefined, true);
        return res.users;
    }
    async createStaff(input) {
        return this.request("POST", "/v1/staff", input, true);
    }
    /** Issues a one-time temporary password for another staff member; they must change it on sign-in. */
    async resetStaffPassword(id) {
        return this.request("POST", `/v1/staff/${encodeURIComponent(id)}/reset-password`, undefined, true);
    }
    async patchStaff(id, input) {
        return this.request("PATCH", `/v1/staff/${encodeURIComponent(id)}`, input, true);
    }
    /** Public request-to-join. Password is hashed server-side; no bearer token is sent. */
    async requestStaffJoin(slug, input) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/staff-requests`, input, false);
    }
    async listStaffJoinRequests(slug, status) {
        const res = await this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/staff-requests${status ? `?status=${status}` : ""}`, undefined, true);
        return res.requests;
    }
    async approveStaffJoinRequest(slug, id, input) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/staff-requests/${encodeURIComponent(id)}/approve`, input, true);
    }
    async rejectStaffJoinRequest(slug, id, input = {}) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/staff-requests/${encodeURIComponent(id)}/reject`, input, true);
    }
    async expireStaffJoinRequest(slug, id) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/staff-requests/${encodeURIComponent(id)}/expire`, undefined, true);
    }
    /** Restores a rejected/expired join request (back to pending). */
    async restoreStaffJoinRequest(slug, id) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/staff-requests/${encodeURIComponent(id)}/restore`, undefined, true);
    }
    async getStaffDashboardCounts(slug) {
        return this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/staff-dashboard`, undefined, true);
    }
    async listAppointmentsStaff() {
        const res = await this.request("GET", "/v1/appointments", undefined, true);
        return res.appointments;
    }
    async acceptAppointment(id) {
        return this.request("POST", `/v1/appointments/${encodeURIComponent(id)}/accept`, undefined, true);
    }
    async declineAppointment(id) {
        return this.request("POST", `/v1/appointments/${encodeURIComponent(id)}/decline`, undefined, true);
    }
    /** Link a booking to a repair order of the same site (400 when the order is not found). */
    async linkAppointmentOrder(id, orderId) {
        return this.request("POST", `/v1/appointments/${encodeURIComponent(id)}/link-order`, { orderId }, true);
    }
    async cancelAppointment(id) {
        return this.request("POST", `/v1/appointments/${encodeURIComponent(id)}/cancel`, undefined, true);
    }
    async listNotifications() {
        const res = await this.request("GET", "/v1/notifications", undefined, true);
        return res.notifications;
    }
    async markNotificationRead(id) {
        return this.request("PATCH", `/v1/notifications/${encodeURIComponent(id)}`, { read: true }, true);
    }
}
//# sourceMappingURL=business.js.map